/**
 * WooCommerce Image Zoom
 * A modern, dependency-free image zoom & gallery plugin
 * Version: 2.0.0
 * License: GPL-2.0+
 */

import DEFAULTS from "./defaults.js";
import utils from "./utils.js";
import domMethods from "./dom.js";
import galleryMethods from "./gallery.js";
import thumbnailMethods from "./thumbnails.js";
import zoomMethods from "./zoom.js";
import touchMethods from "./touch.js";
import lightboxMethods from "./lightbox.js";
import videoMethods from "./video.js";

class WPBeanWCProductGallery {
    constructor(wrapper, options) {
        this.id = utils.uid();
        this.wrapper =
            typeof wrapper === "string"
                ? document.querySelector(wrapper)
                : wrapper;
        if (!this.wrapper) return;

        this.opts = utils.extend({}, DEFAULTS, options || {});
        this.items = [];
        this.index = 0;
        this.isZooming = false;
        this.lbOpen = false;

        // Zoom state
        this._zs = {
            lensX: 0,
            lensY: 0,
            targetX: 0,
            targetY: 0,
            pctX: 0,
            pctY: 0,
            rect: null,
            raf: null,
            scale: 1,
            dist0: 0,
            scale0: 1,
            panX: 0,
            panY: 0,
            panX0: 0,
            panY0: 0,
            originX: 0,
            originY: 0,
            pinching: false,
            mobileZooming: false,
            velX: 0,
            velY: 0,
            lastT: 0,
            lastX: 0,
            lastY: 0,
            momentumRaf: null,
            lastTapT: 0,
            lastTapX: 0,
            lastTapY: 0,
            swipeStartX: 0,
            swipeStartY: 0,
        };
        this._isMobile = window.matchMedia("(max-width: 768px)").matches;
        this._suppressClick = false;
        // Thumb slider state
        this._ts = {
            offset: 0,
            dragging: false,
            startPos: 0,
            startOffset: 0,
        };

        this._resizeHandler = utils.debounce(() => this._onResize(), 120);
        this._scrollHandler = () => {
            if (this.opts.zoomType === "window" && this.isZooming)
                this._positionZoomWindow();
        };
        this._init();
    }

    get _isRTL() {
        return document.documentElement.getAttribute("dir") === "rtl";
    }
}

Object.assign(
    WPBeanWCProductGallery.prototype,
    domMethods,
    galleryMethods,
    thumbnailMethods,
    zoomMethods,
    touchMethods,
    lightboxMethods,
    videoMethods,
);

WPBeanWCProductGallery._lbEl = null;

// ─────────────────────────────────────────────
// Static API
// ─────────────────────────────────────────────
WPBeanWCProductGallery.init = function (selector, options) {
    const els =
        typeof selector === "string"
            ? Array.from(document.querySelectorAll(selector))
            : [selector];
    const out = [];
    els.forEach((el) => {
        if (el._wcpg) {
            el._wcpg.reinit(options);
            out.push(el._wcpg);
        } else {
            const i = new WPBeanWCProductGallery(el, options);
            el._wcpg = i;
            out.push(i);
        }
    });
    return out.length === 1 ? out[0] : out;
};

WPBeanWCProductGallery.reinit = function (selector, options) {
    const els =
        typeof selector === "string"
            ? Array.from(document.querySelectorAll(selector))
            : [selector];
    els.forEach((el) =>
        el._wcpg
            ? el._wcpg.reinit(options)
            : WPBeanWCProductGallery.init(el, options),
    );
};

WPBeanWCProductGallery.destroy = function (selector) {
    const els =
        typeof selector === "string"
            ? Array.from(document.querySelectorAll(selector))
            : [selector];
    els.forEach((el) => {
        if (el._wcpg) {
            el._wcpg.destroy();
            delete el._wcpg;
        }
    });
};

WPBeanWCProductGallery.autoInit = function () {
    document.querySelectorAll("[data-wcpg]").forEach((el) => {
        if (el._wcpg) return;
        let o = {};
        try {
            o = JSON.parse(el.getAttribute("data-wcpg") || "{}");
        } catch (e) {}
        WPBeanWCProductGallery.init(el, o);
    });
};

export default WPBeanWCProductGallery;
